// LOCAL/volatil: captura opcional da aba, sem armazenamento ou envio remoto.
// Somente rotas normalizadas, tempos e IDs aleatorios gerados pelo servidor.
type Outcome = "complete" | "error" | "aborted";
export type VerifiedMilestone = "dashboard_settled" | "current_readings_commit";
export type Milestone = "route_commit" | "widgets_commit" | "diagnostic_commit" | "histories_commit" | "availability_commit" | VerifiedMilestone;
type Entry = { kind: "navigation" | "http" | "milestone" | "longtask"; navigation: number; atMs: number;
  route: string; durationMs?: number; phase?: Milestone; source?: "initial" | "link" | "popstate" | "router";
  status?: number; method?: string; outcome?: Outcome; headersMs?: number; bodyMs?: number;
  requestId?: string; serverMs?: number; dbMs?: number; poolWaitMs?: number; sharedWaitMs?: number; documentMs?: number };
/** Identidade somente interna; nunca vai para o relatorio nem para storage. */
export interface PerformanceNavigationToken { readonly generation: number; readonly navigation: number; readonly path: string }
let enabled = false;
let generation = 0;
let navigation = 0;
let navigationStart = 0;
let origin = 0;
let currentPath = ""; // somente em memoria, jamais incluido no relatorio
let entries: Entry[] = [];
let phases = new Set<Milestone>();
let navigationSource: Entry["source"];
let droppedEntries = 0;
let detach: (() => void) | undefined;
const round = (value: number) => Math.round(Math.max(0, value) * 100) / 100;

export function diagnosticRoute(path: string): string {
  const templates = ["/workflows", "/workflows/:id", "/devices", "/devices/:id", "/configuracoes", "/login",
    "/api/auth/me", "/api/auth/login", "/api/auth/logout", "/api/workspaces", "/api/workspaces/:id",
    "/api/workspaces/:id/structure", "/api/workspaces/:id/dashboard", "/api/devices/discovered",
    "/api/devices/:id/dashboard", "/api/preferences/:id", "/api/devices/:id/production/history",
    "/api/devices/:id/production/histories", "/api/devices/:id/availability", "/api/workspaces/:id/machines/:machine/layout",
    "/api/workspaces/:id/machines/:machine/layout/widgets", "/api/workspaces/:id/machines/:machine/layout/signal-configs",
    "/api/workspaces/:id/devices/:device/ports/:port/production"];
  return templates.find((template) => new RegExp(`^${template.replace(/:[a-z]+/g, "[^/]+")}$`).test(path.split("?")[0])) ?? "other";
}
function add(entry: Entry) {
  if (!enabled) return;
  entries.push(entry);
  if (entries.length > 200) { entries.shift(); droppedEntries++; }
}
export function performanceEnabled() { return enabled; }
export function performanceReport() {
  const documentEntry = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
  const documentTiming = documentEntry ? {
    type: ["navigate", "reload", "back_forward", "prerender"].includes(documentEntry.type) ? documentEntry.type : "other",
    responseStartMs: round(documentEntry.responseStart), responseEndMs: round(documentEntry.responseEnd),
    domContentLoadedMs: round(documentEntry.domContentLoadedEventEnd), loadMs: round(documentEntry.loadEventEnd),
  } : undefined;
  return { version: 2, droppedEntries, documentTiming,
    note: "Tempos de parede; commit nao prova paint nem tempo CPU. widgets_commit pode ser previa. dashboard_settled confirma contexto/configuracao, nao leitura valida; current_readings_commit confirma ao menos uma leitura atual numerica mostrada, nao todos os cards/historicos. documentMs parte do inicio deste documento, sem garantir cache frio. db/pool sao somatorios concorrentes.",
    entries: entries.map((entry) => ({ ...entry })) };
}
export function stopPerformanceCapture() {
  enabled = false;
  generation++;
  entries = [];
  droppedEntries = 0;
  phases.clear();
  currentPath = "";
  detach?.();
  detach = undefined;
}
export function startPerformanceCapture() {
  stopPerformanceCapture();
  enabled = true;
  navigation = 0;
  origin = performance.now();
  beginNavigation(window.location.pathname, "initial");
  const onClick = (event: MouseEvent) => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (!(link instanceof HTMLAnchorElement) || link.download || (link.target && link.target !== "_self")) return;
    const url = new URL(link.href);
    if (url.origin === window.location.origin && url.pathname !== currentPath) beginNavigation(url.pathname, "link");
  };
  const onPopState = () => beginNavigation(window.location.pathname, "popstate");
  document.addEventListener("click", onClick, true);
  window.addEventListener("popstate", onPopState);
  let observer: PerformanceObserver | undefined;
  const epoch = generation;
  // Long task indica bloqueio da thread principal; nao atribui a culpa ao React.
  try {
    if (typeof PerformanceObserver !== "undefined" && PerformanceObserver.supportedEntryTypes.includes("longtask")) {
      observer = new PerformanceObserver((list) => {
        if (!enabled || epoch !== generation) return;
        for (const task of list.getEntries()) {
          const atMs = round(task.startTime - origin);
          const owner = entries.filter((entry) => entry.kind === "navigation" && entry.atMs <= atMs).at(-1);
          add({ kind: "longtask", navigation: owner?.navigation ?? 0, route: owner?.route ?? "other", atMs, durationMs: round(task.duration) });
        }
      });
      observer.observe({ type: "longtask" });
    }
  } catch { observer?.disconnect(); }
  detach = () => {
    document.removeEventListener("click", onClick, true);
    window.removeEventListener("popstate", onPopState);
    observer?.disconnect();
  };
}
export function beginNavigation(path: string, source: Entry["source"]) {
  if (!enabled) return;
  currentPath = path;
  navigation++;
  navigationStart = performance.now();
  navigationSource = source;
  phases = new Set();
  add({ kind: "navigation", navigation, route: diagnosticRoute(path), source, atMs: round(navigationStart - origin) });
}
function addMilestone(phase: Milestone) {
  if (phases.has(phase)) return;
  phases.add(phase);
  const now = performance.now();
  add({ kind: "milestone", navigation, route: diagnosticRoute(currentPath), phase,
    atMs: round(now - origin), durationMs: round(now - navigationStart),
    ...(navigationSource === "initial" ? { documentMs: round(now) } : {}) });
}
export function commitMilestone(phase: Exclude<Milestone, VerifiedMilestone>) {
  if (!enabled) return;
  if (currentPath !== window.location.pathname) beginNavigation(window.location.pathname, "router");
  addMilestone(phase);
}
export function capturePerformanceNavigation(expectedPath: string): PerformanceNavigationToken | undefined {
  if (!enabled || window.location.pathname !== expectedPath) return undefined;
  if (currentPath !== expectedPath) beginNavigation(expectedPath, "router");
  return { generation, navigation, path: expectedPath };
}
export function commitVerifiedMilestone(token: PerformanceNavigationToken | undefined, phase: VerifiedMilestone) {
  if (!enabled || !token || token.generation !== generation || token.navigation !== navigation
    || token.path !== currentPath || token.path !== window.location.pathname) return;
  addMilestone(phase);
}
export function beginHttpTiming(path: string, method = "GET") {
  if (!enabled) return undefined;
  const epoch = generation;
  const start = performance.now();
  const item: Entry = { kind: "http", navigation, route: diagnosticRoute(path), atMs: round(start - origin),
    method: ["GET", "POST", "PUT", "PATCH", "DELETE", "OPTIONS"].includes(method) ? method : "other" };
  let headersAt: number | undefined;
  let finished = false;
  return {
    headers(response: Response) {
      headersAt = performance.now();
      item.headersMs = round(headersAt - start);
      item.status = response.status;
      const requestId = response.headers.get("X-Request-ID");
      if (requestId && /^[a-f0-9-]{36}$/.test(requestId)) item.requestId = requestId;
      const names = { app: "serverMs", db: "dbMs", pool: "poolWaitMs", shared: "sharedWaitMs" } as const;
      const header = response.headers.get("Server-Timing") ?? "";
      for (const match of header.matchAll(/(?:^|,)\s*(app|db|pool|shared);dur=([\d.]+)/g)) {
        const n = Number(match[2]);
        if (Number.isFinite(n) && n >= 0) item[names[match[1] as keyof typeof names]] = n;
      }
    },
    finish(outcome: Outcome) {
      if (finished || epoch !== generation) return;
      finished = true;
      item.outcome = outcome;
      item.durationMs = round(performance.now() - start);
      if (headersAt !== undefined) item.bodyMs = round(performance.now() - headersAt);
      add(item);
    },
  };
}
